/* Free Gift Promo — see CONTEXT.md and docs/adr/0001-free-gift-promo-not-a-pure-cart-function.md */

export const FREE_GIFT_VARIANT_ID = 47427014852660;
export const FREE_GIFT_THRESHOLD_CENTS = 14000; // $140 AUD
// The $140 threshold is AUD-denominated and the promo was scoped for AU only — see #73.
export const FREE_GIFT_ELIGIBLE_MARKET_HANDLE = "au";

export type FreeGiftCustomer = { ordersCount: number } | null;

// Mirrors the shape of SellingPlanAllocation from cart.ts, trimmed to the field we need.
export type MinimalSellingPlanAllocation = { selling_plan?: { id: number } };

export type MinimalCartItem = {
  variant_id: number;
  key?: string;
  line_price?: number;
  selling_plan_allocation?: MinimalSellingPlanAllocation;
};
export type MinimalCart = { items: MinimalCartItem[] };

export function findFreeGiftLine(cart: MinimalCart): MinimalCartItem | undefined {
  return cart.items.find((item) => item.variant_id === FREE_GIFT_VARIANT_ID);
}

export function decideFreeGiftAction({
  subtotalCents,
  customer,
  cart,
  marketHandle,
}: {
  // Shopify's raw items_subtotal_price, including the gift line's own (undiscounted)
  // price when it's already in the cart — the Discount Function that zeroes it out
  // (see docs/adr/0001) only runs at checkout, not here. We net it back out below so
  // the gift's own price can't mask the customer's real spend dropping below threshold.
  subtotalCents: number;
  customer: FreeGiftCustomer;
  cart: MinimalCart;
  // The customer's resolved market handle (window.localization.market.handle — see
  // blocks/_cart-drawer.liquid). The $140 threshold is AUD-denominated and the promo
  // was scoped for AU only, so a non-AU (or unresolved) market is treated the same as
  // any other ineligibility reason. See #73.
  marketHandle: string | null | undefined;
}): "add" | "remove" | "noop" {
  const giftLine = findFreeGiftLine(cart);
  const hasGiftLine = giftLine !== undefined;
  const netSubtotalCents = subtotalCents - (giftLine?.line_price ?? 0);

  // A subscription line (selling_plan_allocation present, same presence check as cart.ts)
  // anywhere in the cart makes the whole cart ineligible for the free gift.
  const hasSubscriptionLine = cart.items.some((item) => Boolean(item.selling_plan_allocation?.selling_plan?.id));

  // A guest (no logged-in customer) can't be checked against order history, so we can't
  // tell them apart from a genuine new customer — treat them as eligible rather than
  // excluding them by default. See #71 (reverses the guest-exclusion from ADR 0001/#55).
  const isEligible =
    (customer === null || customer.ordersCount === 0) &&
    !hasSubscriptionLine &&
    marketHandle?.toLowerCase() === FREE_GIFT_ELIGIBLE_MARKET_HANDLE;
  const isOverThreshold = netSubtotalCents >= FREE_GIFT_THRESHOLD_CENTS;

  if (!hasGiftLine && isEligible && isOverThreshold) return "add";
  if (hasGiftLine && (!isEligible || !isOverThreshold)) return "remove";
  return "noop";
}
